import { useState, useEffect } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import api from '../../services/api'
import {
  Layers,
  CheckCircle2,
  Clock,
  AlertCircle,
  FileText,
  Upload,
  Download,
  ChevronRight,
  ExternalLink,
  Sparkles,
  ArrowRight,
  Shield,
  User,
  Calendar,
  Check,
  Eye,
  RefreshCw,
  Printer,
} from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import ProjectBriefPrintable from '../../components/projectlab/ProjectBriefPrintable'
import { buildProjectBriefModel, briefHasContent } from '../../utils/projectBrief'

export default function StudentProjectLab() {
  const { enrollmentId: paramEnrollmentId } = useParams()
  const navigate = useNavigate()

  // Explicit load state. Stage-dependent UI is never rendered until the
  // workspace for the *selected* enrollment has loaded with its stages.
  const [enrollments, setEnrollments] = useState([])
  const [enrollmentsStatus, setEnrollmentsStatus] = useState('loading') // loading | ready | error
  const [projectData, setProjectData] = useState(null)
  const [projectStatus, setProjectStatus] = useState('idle') // idle | loading | ready | error
  const [projectError, setProjectError] = useState(null)
  const [error, setError] = useState(null)
  const [actionLoading, setActionLoading] = useState(false)
  const [actionSuccess, setActionSuccess] = useState(null)

  // Submission Form State
  const [selectedFiles, setSelectedFiles] = useState([])
  const [participantNotes, setParticipantNotes] = useState('')
  const [checklistAgreed, setChecklistAgreed] = useState(false)
  const [exportingBrief, setExportingBrief] = useState(false)

  // Load the participant's enrollments once.
  useEffect(() => {
    let cancelled = false
    api.get('/project-lab/student/enrollments')
      .then((res) => {
        if (cancelled) return
        setEnrollments(res.data?.enrollments || [])
        setEnrollmentsStatus('ready')
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[StudentProjectLab] Failed to load enrollments:', err)
        setEnrollmentsStatus('error')
      })
    return () => { cancelled = true }
  }, [])

  // The selected enrollment is derived from the route, never guessed:
  //   /student/project-lab            → primary enrollment (active first)
  //   /student/project-lab/:id        → that enrollment, or "invalid" if not the participant's
  const activeEnrollment = paramEnrollmentId
    ? enrollments.find((e) => String(e.id) === String(paramEnrollmentId)) || null
    : enrollments.find((e) => e.status === 'active' || e.status === 'in_progress') || enrollments[0] || null
  const activeEnrollmentId = activeEnrollment?.id ?? null

  // Load the workspace for the selected enrollment.
  useEffect(() => {
    if (!activeEnrollmentId) return
    let cancelled = false
    setProjectData(null)
    setProjectError(null)
    setProjectStatus('loading')
    api.get(`/project-lab/student/projects/${activeEnrollmentId}`)
      .then((res) => {
        if (cancelled) return
        setProjectData(res.data)
        setProjectStatus('ready')
      })
      .catch((err) => {
        if (cancelled) return
        console.error('[StudentProjectLab] Failed to load workspace:', err)
        setProjectError(err.response?.data?.error || 'Failed to load project details.')
        setProjectStatus('error')
      })
    return () => { cancelled = true }
  }, [activeEnrollmentId])

  // Handle acknowledge-mode stage completion (e.g. Brief review & acceptance)
  const handleAdvanceBrief = async (stageIndex) => {
    try {
      setActionLoading(true)
      setActionSuccess(null)
      setError(null)
      await api.post(`/project-lab/student/projects/${activeEnrollmentId}/stages/${stageIndex}/advance-brief`)
      setActionSuccess(`Stage ${stageIndex} acknowledged and accepted.`)
      // Refresh project
      const refreshRes = await api.get(`/project-lab/student/projects/${activeEnrollmentId}`)
      setProjectData(refreshRes.data)
    } catch (err) {
      console.error('[StudentProjectLab] Advance brief error:', err)
      setError(err.response?.data?.error || 'Failed to advance stage.')
    } finally {
      setActionLoading(false)
    }
  }

  // Handle checklist-mode stage confirmation
  const handleAdvanceChecklist = async (stageIndex) => {
    try {
      setActionLoading(true)
      setActionSuccess(null)
      setError(null)
      await api.post(`/project-lab/student/projects/${activeEnrollmentId}/stages/${stageIndex}/advance-checklist`, {
        participant_notes: participantNotes,
      })
      setActionSuccess(`Stage ${stageIndex} confirmation recorded and submitted for mentor review.`)
      setParticipantNotes('')
      // Refresh
      const refreshRes = await api.get(`/project-lab/student/projects/${activeEnrollmentId}`)
      setProjectData(refreshRes.data)
    } catch (err) {
      console.error('[StudentProjectLab] Checklist advance error:', err)
      setError(err.response?.data?.error || 'Failed to submit checklist.')
    } finally {
      setActionLoading(false)
    }
  }

  // Handle submission-mode stage file upload
  const handleSubmitWork = async (stage) => {
    const stageIndex = stage.stage_index
    if (selectedFiles.length === 0) {
      setError('Please select at least one file to upload.')
      return
    }
    if (stage.max_files && selectedFiles.length > stage.max_files) {
      setError(`This stage accepts at most ${stage.max_files} files.`)
      return
    }
    if (stage.submission_notes_required && !participantNotes.trim()) {
      setError('Submission notes are required for this stage.')
      return
    }

    try {
      setActionLoading(true)
      setActionSuccess(null)
      setError(null)

      const formData = new FormData()
      formData.append('participant_notes', participantNotes)
      for (const file of selectedFiles) {
        formData.append('files', file)
      }

      await api.post(
        `/project-lab/student/projects/${activeEnrollmentId}/stages/${stageIndex}/submit`,
        formData,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        }
      )

      setActionSuccess(`Stage ${stageIndex} work submitted successfully! Awaiting mentor review.`)
      setSelectedFiles([])
      setParticipantNotes('')

      // Refresh
      const refreshRes = await api.get(`/project-lab/student/projects/${activeEnrollmentId}`)
      setProjectData(refreshRes.data)
    } catch (err) {
      console.error('[StudentProjectLab] Submit work error:', err)
      setError(err.response?.data?.error || 'Failed to submit work.')
    } finally {
      setActionLoading(false)
    }
  }

  if (enrollmentsStatus === 'loading') {
    return <LoadingSpinner label="Loading Project Lab workspace..." />
  }

  if (enrollmentsStatus === 'error') {
    return (
      <WorkspaceNotice
        title="Project Lab unavailable"
        message="We could not load your Project Lab enrollment. Please try again shortly."
      />
    )
  }

  if (enrollments.length === 0) {
    return (
      <div className="space-y-6">
        <div className="cinema-page-header">
          <span className="cinema-label block mb-2">Workspace</span>
          <h1 className="font-display text-2xl sm:text-3xl text-white font-light">Project Lab</h1>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-none p-8 text-center max-w-xl mx-auto">
          <Layers className="w-12 h-12 text-cinema-gold mx-auto mb-4 opacity-80" />
          <h3 className="font-display text-lg text-white font-medium mb-2">No Active Enrollments</h3>
          <p className="text-sm text-cinema-muted mb-6">
            You are not currently enrolled in any commercial Project Lab programs. Explore our live projects to apply.
          </p>
          <Link
            to="/project-lab"
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-cinema-gold text-black font-medium text-sm rounded-none hover:bg-yellow-400 transition-colors"
          >
            Explore Project Lab <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    )
  }

  if (!activeEnrollment) {
    return (
      <WorkspaceNotice
        title="Project not found"
        message="This Project Lab enrollment does not exist or is not linked to your account."
        linkTo="/student/project-lab"
        linkLabel="Open my project"
      />
    )
  }

  const workspaceLoaded =
    projectStatus === 'ready' && String(projectData?.enrollment?.id) === String(activeEnrollmentId)

  if (projectStatus === 'error') {
    return (
      <WorkspaceNotice
        title="Project workspace unavailable"
        message={projectError}
        linkTo="/student/dashboard"
        linkLabel="Back to my project"
      />
    )
  }

  if (!workspaceLoaded) {
    return <LoadingSpinner label="Loading Project Lab workspace..." />
  }

  const enrollment = projectData.enrollment
  const project = projectData.project
  const mentor = projectData.mentor
  const stages = Array.isArray(projectData.stages) ? projectData.stages : []
  const submissions = projectData.submissions || []
  const currentStageIndex = enrollment.current_stage_index || 1
  const activeStage = stages.find((s) => s.stage_index === currentStageIndex)

  if (!activeStage) {
    // Enrollment exists but its stage progress does not include the current stage.
    console.error('[StudentProjectLab] Stage data missing for enrollment', enrollment.id, 'stage', currentStageIndex)
    return (
      <WorkspaceNotice
        title="Stage data unavailable"
        message={`${project?.title || 'This project'} has no data for stage ${currentStageIndex}. Please contact your mentor or SMA support.`}
        linkTo="/student/dashboard"
        linkLabel="Back to my project"
      />
    )
  }

  const guide = activeStage.guide || {}
  const isCompleted = enrollment?.status === 'completed'

  // Semantic completion mode drives the workspace pane (never stage index)
  const completionMode = activeStage.completion_mode
    || (activeStage.requires_files ? 'submission' : currentStageIndex === 1 ? 'acknowledge' : 'checklist')
  const briefSections = projectData.brief?.sections || []

  // The Project Brief document — built from this very workspace payload, so
  // the page, the print view and the PDF can never disagree.
  const briefModel = buildProjectBriefModel(projectData)
  const canExportBrief = briefHasContent(briefModel)

  const handleDownloadBrief = async () => {
    if (!canExportBrief || exportingBrief) return
    setExportingBrief(true)
    try {
      const { downloadProjectBriefPDF } = await import('../../utils/projectBriefPdf')
      await downloadProjectBriefPDF(briefModel)
    } catch (err) {
      console.error('[StudentProjectLab] Brief export failed:', err)
      setError('Could not generate the project brief. Please try again.')
    } finally {
      setExportingBrief(false)
    }
  }

  const handlePrintBrief = () => {
    if (!canExportBrief) return
    window.print()
  }
  const stageGuidance = activeStage.guidance?.length ? activeStage.guidance : (guide.guidance || [])
  const stageRequirements = activeStage.requirements || []
  const stageAcceptance = activeStage.acceptance_criteria || []

  return (
    <div className="space-y-8 pb-16">
      {/* Top Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10 pb-6">
        {/* min-w-0 so a long project title shrinks this flex column instead of
            pushing the row — and the header — past the viewport. */}
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className="cinema-label text-xs">SMA Project Lab</span>
            <span className="text-white/20">•</span>
            <span className="text-xs text-cinema-gold capitalize font-medium">{enrollment?.mode} mode</span>
            {isCompleted && (
              <span className="text-[11px] px-2 py-0.5 rounded-none bg-emerald-500/20 text-emerald-400 border border-emerald-500/30">
                Completed
              </span>
            )}
          </div>
          <h1 className="font-display text-2xl sm:text-3xl text-white font-light tracking-tight break-words">
            {project?.title}
          </h1>
          <p className="text-xs sm:text-sm text-cinema-muted mt-1 max-w-2xl">
            {project?.short_summary || project?.final_deliverable}
          </p>
          {enrollments.length > 1 && (
            <div className="flex items-center gap-2 mt-3">
              <span className="text-[10px] text-cinema-muted uppercase tracking-wider font-mono">Project:</span>
              <select
                value={activeEnrollmentId || ''}
                onChange={(e) => {
                  navigate(`/student/project-lab/${e.target.value}`)
                }}
                className="bg-white/5 border border-white/10 text-xs text-white px-2.5 py-1 rounded-none focus:border-cinema-gold focus:outline-none"
              >
                {enrollments.map((enr) => (
                  <option key={enr.id} value={enr.id} className="bg-black text-white">
                    {enr.project_title || enr.title} ({enr.status})
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        {/* Brief document actions + Mentor Card */}
        <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row items-stretch sm:items-center gap-3 w-full md:w-auto shrink-0">
          {canExportBrief && (
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <button
                onClick={handleDownloadBrief}
                disabled={exportingBrief}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] text-xs font-semibold uppercase tracking-wider bg-cinema-gold text-black rounded-none hover:bg-yellow-400 transition-colors disabled:opacity-60"
              >
                <Download className="w-4 h-4 shrink-0" />
                <span className="whitespace-nowrap">{exportingBrief ? 'Preparing…' : 'Download Brief'}</span>
              </button>
              <button
                onClick={handlePrintBrief}
                className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[44px] text-xs font-semibold uppercase tracking-wider border border-white/15 text-cinema-soft hover:text-white hover:border-cinema-gold/50 rounded-none transition-colors"
              >
                <Printer className="w-4 h-4 shrink-0" />
                <span className="whitespace-nowrap">Print Brief</span>
              </button>
            </div>
          )}

        {mentor && (
          <div className="flex items-center gap-3 bg-white/[0.03] border border-white/10 rounded-none px-4 py-3 self-start min-w-0">
            <div className="w-9 h-9 rounded-none bg-cinema-gold/20 border border-cinema-gold/30 flex items-center justify-center text-cinema-gold font-bold text-xs">
              {mentor.first_name?.[0]}{mentor.last_name?.[0]}
            </div>
            <div className="min-w-0">
              <span className="text-[10px] uppercase tracking-wider text-cinema-muted block">Assigned Mentor</span>
              <span className="text-xs font-medium text-white block truncate">{mentor.first_name} {mentor.last_name}</span>
            </div>
          </div>
        )}
        </div>
      </div>

      {/* Notifications / Alerts */}
      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-none p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
          <div className="text-sm text-red-200">{error}</div>
        </div>
      )}

      {actionSuccess && (
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-none p-4 flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0 mt-0.5" />
          <div className="text-sm text-emerald-200">{actionSuccess}</div>
        </div>
      )}

      {/* ─── SECTION 7: CORE THREE QUESTIONS HERO ───────────────────────── */}
      <div className="bg-gradient-to-br from-white/[0.04] to-white/[0.01] border border-white/10 rounded-none p-6 lg:p-8 relative overflow-hidden shadow-xl">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 divide-y lg:divide-y-0 lg:divide-x divide-white/10">
          
          {/* Question 1: Where am I? */}
          <div className="space-y-2 lg:pr-6">
            <span className="cinema-label text-[11px] uppercase tracking-widest text-cinema-gold block">
              Where am I?
            </span>
            <div className="text-xl sm:text-2xl font-display font-medium text-white">
              Stage {currentStageIndex} — {activeStage.title}
            </div>
            <div className="flex items-center gap-2 pt-1">
              <span className="text-xs text-cinema-muted">Status:</span>
              <span
                className={`text-xs px-2.5 py-0.5 rounded-none border font-medium capitalize ${
                  activeStage.status === 'approved' || activeStage.status === 'completed'
                    ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                    : activeStage.status === 'submitted'
                    ? 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                    : activeStage.status === 'needs_revision'
                    ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                    : 'bg-yellow-500/10 text-yellow-400 border-yellow-500/30'
                }`}
              >
                {activeStage.status?.replace('_', ' ')}
              </span>
            </div>
          </div>

          {/* Question 2: What am I supposed to do? */}
          <div className="space-y-2 pt-4 lg:pt-0 lg:px-6">
            <span className="cinema-label text-[11px] uppercase tracking-widest text-cinema-gold block">
              What am I supposed to do?
            </span>
            <p className="text-sm text-cinema-soft leading-relaxed">
              {guide.objective || 'Follow stage guidance and submit requirements.'}
            </p>
          </div>

          {/* Question 3: What do I do next? */}
          <div className="space-y-3 pt-4 lg:pt-0 lg:pl-6 flex flex-col justify-center">
            <span className="cinema-label text-[11px] uppercase tracking-widest text-cinema-gold block">
              What do I do next?
            </span>
            <div>
              {activeStage.status === 'submitted' ? (
                <div className="flex items-center gap-2 text-cyan-400 text-sm font-medium">
                  <Clock className="w-4 h-4 animate-pulse" />
                  <span>Work submitted. Awaiting mentor feedback.</span>
                </div>
              ) : activeStage.status === 'needs_revision' ? (
                <div className="text-amber-400 text-sm font-medium flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>Review feedback below and submit revised work.</span>
                </div>
              ) : isCompleted ? (
                <Link
                  to="#portfolio"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-none text-sm font-medium hover:bg-emerald-500 transition-colors"
                >
                  <Sparkles className="w-4 h-4" /> View Completed Portfolio
                </Link>
              ) : (
                <span className="text-white text-sm font-medium">
                  {guide.actionLabel || 'Proceed with current stage requirements'}
                </span>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ─── PRODUCTION LIFECYCLE TIMELINE / STEPPER ─────────────────────── */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-xs uppercase tracking-widest text-cinema-muted font-mono">Production Lifecycle</h2>
          <span className="text-xs text-cinema-muted font-mono">{currentStageIndex} of {stages.length} Stages</span>
        </div>

        {/* Stage count is data-driven; the 8-column density cap is a layout
            choice only — projects with more stages wrap onto additional rows. */}
        <div
          className="grid grid-cols-2 sm:grid-cols-4 gap-2 lg:[grid-template-columns:repeat(var(--stage-cols),minmax(0,1fr))]"
          style={{ '--stage-cols': Math.min(stages.length, 8) }}
        >
          {stages.map((stage) => {
            const isCurrent = stage.stage_index === currentStageIndex
            const isApproved = stage.status === 'approved' || stage.status === 'completed'
            const isSubmitted = stage.status === 'submitted'
            const isRevision = stage.status === 'needs_revision'

            return (
              <div
                key={stage.stage_index}
                className={`border p-3 rounded-none transition-all ${
                  isCurrent
                    ? 'border-cinema-gold bg-cinema-gold/10'
                    : isApproved
                    ? 'border-emerald-500/30 bg-emerald-500/5'
                    : isSubmitted
                    ? 'border-cyan-500/30 bg-cyan-500/5'
                    : isRevision
                    ? 'border-amber-500/30 bg-amber-500/5'
                    : 'border-white/8 bg-white/[0.01] opacity-60'
                }`}
              >
                <div className="flex items-center justify-between mb-1">
                  <span className="text-[10px] font-mono text-cinema-muted">{String(stage.stage_index).padStart(2, '0')}</span>
                  {isApproved ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />
                  ) : isSubmitted ? (
                    <Clock className="w-3.5 h-3.5 text-cyan-400 animate-pulse" />
                  ) : isRevision ? (
                    <AlertCircle className="w-3.5 h-3.5 text-amber-400" />
                  ) : isCurrent ? (
                    <span className="w-2 h-2 rounded-none bg-cinema-gold" />
                  ) : null}
                </div>
                <div className="text-xs font-medium text-white truncate">{stage.title}</div>
                <div className="text-[10px] text-cinema-muted capitalize mt-0.5">
                  {stage.status?.replace('_', ' ')}
                </div>
              </div>
            )
          })}
        </div>
      </div>

      {/* ─── ACTIVE STAGE WORKSPACE PANE ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* Left 2 Cols: Stage Guidance & Production Actions */}
        <div className="lg:col-span-2 space-y-6">

          {/* Mentor Feedback Banner if Revision Requested */}
          {activeStage.status === 'needs_revision' && activeStage.mentor_feedback && (
            <div className="bg-amber-500/10 border border-amber-500/40 rounded-none p-5 space-y-2">
              <div className="flex items-center gap-2 text-amber-400 font-medium text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>Mentor Revision Request</span>
              </div>
              <p className="text-xs sm:text-sm text-amber-200/90 leading-relaxed pl-6 break-words">
                {activeStage.mentor_feedback}
              </p>
            </div>
          )}

          {/* Mentor Approval Banner */}
          {(activeStage.status === 'approved' || activeStage.status === 'completed') && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-none p-5 space-y-2">
              <div className="flex items-center gap-2 text-emerald-400 font-medium text-sm">
                <CheckCircle2 className="w-4 h-4" />
                <span>Stage Approved</span>
              </div>
              {activeStage.mentor_feedback && (
                <p className="text-xs sm:text-sm text-emerald-200/90 leading-relaxed pl-6">
                  {activeStage.mentor_feedback}
                </p>
              )}
            </div>
          )}

          {/* Stage Details & Objectives Card */}
          <div className="bg-white/[0.02] border border-white/10 rounded-none p-6 space-y-6">
            <div className="border-b border-white/10 pb-4">
              <span className="cinema-label text-[11px] uppercase tracking-widest text-cinema-gold block mb-1">
                Stage {currentStageIndex} Guidance
              </span>
              <h3 className="font-display text-xl text-white font-light">
                {activeStage.title} Requirements
              </h3>
            </div>

            {/* acknowledge mode: authored brief sections + accept action */}
            {completionMode === 'acknowledge' && (
              <div className="space-y-6">
                {briefSections.length > 0 ? (
                  <>
                    <div className="bg-white/[0.02] border border-white/8 rounded-none p-5 space-y-3">
                      <h4 className="text-xs font-mono uppercase tracking-wider text-cinema-gold">{briefSections[0].title}</h4>
                      <p className="text-sm text-cinema-soft leading-relaxed break-words">
                        {briefSections[0].content}
                      </p>
                    </div>

                    {briefSections.length > 2 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {briefSections.slice(1, -1).map((section) => (
                          <div key={section.id || section.section_key} className="bg-white/[0.02] border border-white/8 rounded-none p-4 space-y-1.5">
                            <h5 className="text-[11px] font-mono uppercase tracking-wider text-cinema-muted">{section.title}</h5>
                            <p className="text-xs text-cinema-soft leading-relaxed break-words">{section.content}</p>
                          </div>
                        ))}
                      </div>
                    )}

                    {briefSections.length > 1 && (
                      <div className="bg-white/[0.02] border border-white/8 rounded-none p-4 space-y-1.5">
                        <h5 className="text-[11px] font-mono uppercase tracking-wider text-cinema-muted">{briefSections[briefSections.length - 1].title}</h5>
                        <p className="text-xs text-cinema-soft leading-relaxed">{briefSections[briefSections.length - 1].content}</p>
                      </div>
                    )}
                  </>
                ) : stageGuidance.length > 0 ? (
                  <ul className="space-y-2.5">
                    {stageGuidance.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-cinema-soft leading-relaxed">
                        <Check className="w-4 h-4 text-cinema-gold shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-cinema-soft leading-relaxed">{activeStage.description || activeStage.objective}</p>
                )}

                {/* Primary acknowledge action */}
                <div className="pt-4 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <span className="text-xs text-cinema-muted min-w-0">
                    No file upload is required for this stage.
                  </span>
                  <button
                    onClick={() => handleAdvanceBrief(currentStageIndex)}
                    disabled={actionLoading}
                    className="w-full sm:w-auto px-6 py-3 min-h-[44px] bg-cinema-gold text-black font-semibold text-sm rounded-none hover:bg-yellow-400 transition-colors shadow-lg shadow-cinema-gold/10 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? 'Advancing...' : (activeStage.action_label || guide.actionLabel || 'Accept & Continue')} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}

            {/* checklist mode: guidance checklist + confirmation notes */}
            {completionMode === 'checklist' && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-cinema-gold">Standard Operational Guide</h4>
                  <ul className="space-y-2.5">
                    {stageGuidance.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-cinema-soft leading-relaxed">
                        <Check className="w-4 h-4 text-cinema-gold shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {stageRequirements.length > 0 && (
                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <h4 className="text-xs font-mono uppercase tracking-wider text-cinema-gold">Requirements</h4>
                    <ul className="space-y-2">
                      {stageRequirements.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-cinema-soft leading-relaxed">
                          <ChevronRight className="w-4 h-4 text-cinema-gold shrink-0 mt-0.5" />
                          <span className="min-w-0 break-words">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {stageAcceptance.length > 0 && (
                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <h4 className="text-xs font-mono uppercase tracking-wider text-cinema-gold">Acceptance Criteria</h4>
                    <ul className="space-y-2">
                      {stageAcceptance.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-cinema-soft leading-relaxed">
                          <Shield className="w-4 h-4 text-cinema-gold shrink-0 mt-0.5" />
                          <span className="min-w-0 break-words">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="space-y-2 pt-4 border-t border-white/10">
                  <label className="block text-xs font-medium text-cinema-soft">
                    Session Log & Confirmation Notes
                  </label>
                  <textarea
                    rows={3}
                    placeholder="Log technical setup notes, challenges faced, or capture frame counts..."
                    value={participantNotes}
                    onChange={(e) => setParticipantNotes(e.target.value)}
                    className="w-full bg-white/[0.03] border border-white/10 rounded-none p-3 text-xs text-white placeholder-cinema-muted focus:outline-none focus:border-cinema-gold"
                  />
                </div>

                <div className="pt-2 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <span className="text-xs text-cinema-muted min-w-0">
                    Submits confirmation to assigned mentor.
                  </span>
                  <button
                    onClick={() => handleAdvanceChecklist(currentStageIndex)}
                    disabled={actionLoading || activeStage.status === 'submitted'}
                    className="w-full sm:w-auto px-6 py-3 min-h-[44px] bg-cinema-gold text-black font-semibold text-sm rounded-none hover:bg-yellow-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {actionLoading ? 'Recording...' : (activeStage.action_label || guide.actionLabel || 'Confirm & Submit')}
                  </button>
                </div>
              </div>
            )}

            {/* submission mode: guidance + file submission pane */}
            {completionMode === 'submission' && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <h4 className="text-xs font-mono uppercase tracking-wider text-cinema-gold">Production Guide</h4>
                  <ul className="space-y-2.5">
                    {stageGuidance.map((item, idx) => (
                      <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-cinema-soft leading-relaxed">
                        <ChevronRight className="w-4 h-4 text-cinema-gold shrink-0 mt-0.5" />
                        <span className="min-w-0 break-words">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {stageRequirements.length > 0 && (
                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <h4 className="text-xs font-mono uppercase tracking-wider text-cinema-gold">Requirements</h4>
                    <ul className="space-y-2">
                      {stageRequirements.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-cinema-soft leading-relaxed">
                          <ChevronRight className="w-4 h-4 text-cinema-gold shrink-0 mt-0.5" />
                          <span className="min-w-0 break-words">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {stageAcceptance.length > 0 && (
                  <div className="space-y-3 pt-4 border-t border-white/10">
                    <h4 className="text-xs font-mono uppercase tracking-wider text-cinema-gold">Acceptance Criteria</h4>
                    <ul className="space-y-2">
                      {stageAcceptance.map((item, idx) => (
                        <li key={idx} className="flex items-start gap-2.5 text-xs sm:text-sm text-cinema-soft leading-relaxed">
                          <Shield className="w-4 h-4 text-cinema-gold shrink-0 mt-0.5" />
                          <span className="min-w-0 break-words">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Submission Form (Only if actionable) */}
                {activeStage.status !== 'approved' && activeStage.status !== 'completed' && (
                  <div className="space-y-4 pt-4 border-t border-white/10">
                    <h4 className="text-xs font-mono uppercase tracking-wider text-white">
                      {activeStage.status === 'needs_revision' ? 'Submit Revised Work' : 'Submit Stage Work'}
                    </h4>

                    {/* File Dropzone */}
                    <div className="border-2 border-dashed border-white/15 rounded-none p-6 text-center hover:border-cinema-gold/40 transition-colors">
                      <Upload className="w-8 h-8 text-cinema-gold mx-auto mb-2 opacity-80" />
                      <label className="cursor-pointer">
                        <span className="text-xs font-medium text-white hover:underline">
                          Choose files to upload
                        </span>
                        <input
                          type="file"
                          multiple
                          onChange={(e) => setSelectedFiles(Array.from(e.target.files))}
                          className="hidden"
                        />
                      </label>
                      <p className="text-[11px] text-cinema-muted mt-1">
                        High-resolution images, PDF shoot plans, or delivery packages (up to 100MB per file)
                      </p>
                      {selectedFiles.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2 justify-center">
                          {selectedFiles.map((f, i) => (
                            <span key={i} className="max-w-full break-all text-[11px] px-2.5 py-1 rounded-none bg-white/10 text-white font-mono">
                              {f.name} ({(f.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Participant Notes */}
                    <div className="space-y-1.5">
                      <label className="block text-xs font-medium text-cinema-soft">
                        Submission Notes / Technical Description
                      </label>
                      <textarea
                        rows={3}
                        placeholder="Detail lighting decisions, camera settings, retouching techniques applied..."
                        value={participantNotes}
                        onChange={(e) => setParticipantNotes(e.target.value)}
                        className="w-full bg-white/[0.03] border border-white/10 rounded-none p-3 text-xs text-white placeholder-cinema-muted focus:outline-none focus:border-cinema-gold"
                      />
                    </div>

                    {/* Action Button */}
                    <div className="pt-2 flex justify-stretch sm:justify-end">
                      <button
                        onClick={() => handleSubmitWork(activeStage)}
                        disabled={actionLoading || selectedFiles.length === 0}
                        className="w-full sm:w-auto px-6 py-3 min-h-[44px] bg-cinema-gold text-black font-semibold text-sm rounded-none hover:bg-yellow-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {actionLoading ? 'Uploading...' : (activeStage.action_label || guide.actionLabel || 'Submit Work')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}

          </div>

        </div>

        {/* Right Col: Workspace Details & Submission Audit Trail */}
        <div className="space-y-6">

          {/* Digital Lab Workspace Link */}
          <div className="bg-white/[0.02] border border-white/10 rounded-none p-5 space-y-3">
            <span className="cinema-label text-[10px] uppercase tracking-wider text-cinema-gold block">
              Digital Lab Workspace
            </span>
            <div className="font-mono text-xs text-white truncate">
              {projectData?.digital_lab_project?.workspace_identifier || 'Not yet created — initializes on first use'}
            </div>
            <p className="text-[11px] text-cinema-muted">
              Connected to Digital Lab workspace and authoritative submission history.
            </p>
          </div>

          {/* Authoritative Submissions History (Constraint 2) */}
          <div className="bg-white/[0.02] border border-white/10 rounded-none p-5 space-y-4">
            <div className="flex items-center justify-between border-b border-white/10 pb-3">
              <span className="cinema-label text-[11px] uppercase tracking-wider text-white">
                Submission History
              </span>
              <span className="text-[11px] text-cinema-muted font-mono">{submissions.length} Total</span>
            </div>

            {submissions.length === 0 ? (
              <p className="text-xs text-cinema-muted italic py-2">No submissions recorded yet.</p>
            ) : (
              <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
                {submissions.map((sub) => (
                  <div key={sub.id} className="bg-white/[0.02] border border-white/8 rounded-none p-3 space-y-2">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span className="text-xs font-semibold text-white min-w-0 break-words">
                        Stage {sub.stage_index}: {sub.stage_name} (v{sub.version})
                      </span>
                      <span
                        className={`text-[10px] px-2 py-0.5 rounded-none border font-medium capitalize ${
                          sub.status === 'approved'
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30'
                            : sub.status === 'needs_changes'
                            ? 'bg-amber-500/10 text-amber-400 border-amber-500/30'
                            : 'bg-cyan-500/10 text-cyan-400 border-cyan-500/30'
                        }`}
                      >
                        {sub.status?.replace('_', ' ')}
                      </span>
                    </div>

                    {sub.participant_notes && (
                      <p className="text-[11px] text-cinema-soft italic border-l-2 border-white/10 pl-2 break-words">
                        "{sub.participant_notes}"
                      </p>
                    )}

                    {/* Files list */}
                    {sub.files && sub.files.length > 0 && (
                      <div className="space-y-1 pt-1">
                        {sub.files.map((file) => (
                          <div key={file.id} className="flex items-center justify-between gap-2 text-[11px] text-cinema-muted">
                            <span className="min-w-0 flex-1 truncate font-mono" title={file.original_filename}>{file.original_filename}</span>
                            <a
                              href={`/api/project-lab/files/${file.id}/download`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-cinema-gold hover:underline flex items-center gap-1 shrink-0"
                            >
                              <Download className="w-3 h-3" /> Get
                            </a>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Feedback */}
                    {sub.feedback && (
                      <div className="pt-2 border-t border-white/5">
                        <span className="text-[10px] uppercase tracking-wider text-cinema-gold block font-mono">
                          Mentor Feedback
                        </span>
                        <p className="text-xs text-cinema-soft mt-0.5 leading-relaxed break-words">
                          {sub.feedback}
                        </p>
                      </div>
                    )}

                    <div className="text-[10px] text-cinema-muted font-mono text-right pt-1">
                      {new Date(sub.created_at).toLocaleDateString()}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

        </div>

      </div>

      {/* ─── SECTION 13: PORTFOLIO SHOWCASE (If Completed) ──────────────── */}
      {isCompleted && (
        <div id="portfolio" className="border-t border-white/10 pt-10 space-y-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <span className="cinema-label text-xs block mb-1">Final Showcase</span>
              <h3 className="font-display text-2xl text-white font-light">Client Portfolio Gallery</h3>
            </div>
            <span className="text-xs px-3 py-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 rounded-none font-medium">
              Verified Delivery Package
            </span>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {submissions
              .flatMap((s) => s.files || [])
              .filter((f) => f.is_final_deliverable)
              .map((file) => (
                <div key={file.id} className="bg-white/[0.02] border border-white/10 rounded-none p-4 flex flex-col justify-between space-y-3">
                  <div className="space-y-1">
                    <FileText className="w-8 h-8 text-cinema-gold opacity-70" />
                    <div className="text-xs font-mono font-medium text-white truncate" title={file.original_filename}>
                      {file.original_filename}
                    </div>
                    <span className="text-[10px] text-cinema-muted font-mono">
                      {(file.size_bytes / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>
                  <a
                    href={`/api/project-lab/files/${file.id}/download`}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full py-2 bg-white/5 hover:bg-white/10 text-white rounded-none text-center text-xs font-medium transition-colors flex items-center justify-center gap-1.5"
                  >
                    <Download className="w-3.5 h-3.5" /> Download Deliverable
                  </a>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* The Project Brief as a document — hidden on screen, laid out on A4
          by styles/print.css when the participant prints. Same model the PDF
          exporter draws. */}
      <ProjectBriefPrintable model={briefModel} />

    </div>
  )
}

function WorkspaceNotice({ title, message, linkTo = '/student/dashboard', linkLabel = 'Back to Project Lab' }) {
  return (
    <div className="max-w-xl border-t border-white/10 pt-8">
      <span className="text-[10px] uppercase tracking-[0.2em] text-cinema-muted font-medium block mb-3">
        Project Lab
      </span>
      <h1 className="font-display text-2xl text-white font-light">{title}</h1>
      {message && <p className="text-sm text-cinema-muted mt-3 leading-relaxed">{message}</p>}
      <Link
        to={linkTo}
        className="inline-flex items-center gap-2 mt-8 text-sm text-cinema-gold hover:text-white transition-colors"
      >
        {linkLabel} <ArrowRight className="w-4 h-4" />
      </Link>
    </div>
  )
}
